import { X, Save, AlertTriangle } from 'lucide-react';
import { colors } from '../styles/colors';
import { useFactureDetails } from '../hooks/useFactureDetails';
import ModalPortal from './ModalPortal';
import FactureDetailTable from './facturation/FactureDetailTable';
import FactureTotaux from './facturation/FactureTotaux';

/**
 * FactureDetailModal - Modale de détail d'une facture externe
 *
 * Refactorisé pour utiliser les composants extraits:
 * - useFactureDetails: hook de gestion de l'état et des handlers
 * - FactureDetailTable: tableau réutilisable pour temps/ajustements
 * - FactureTotaux: section des totaux
 */
function FactureDetailModal({ isOpen, onClose, numeroFacture, onSave, readOnly = false }) {
  const {
    details,
    effectiveDetails,
    tempsDetails,
    ajustementDetails,
    hasStoredDetails,
    isManual,
    loading,
    saving,
    tauxTVA,
    setTauxTVA,
    editingIndex,
    intitule,
    setIntitule,
    statut,
    totals,
    shouldShowNoDetails,
    montantHTManual,
    setMontantHTManual,
    tauxTVAManual,
    setTauxTVAManual,
    dateEmissionManual,
    setDateEmissionManual,
    handleAddTempsLine,
    handleAddAjustementLine,
    handleDeleteLine,
    handleEditLine,
    handleSaveLine,
    handleFieldChange,
    handleSave,
    handleSaveManuel,
    handleClose
  } = useFactureDetails(numeroFacture, isOpen, onClose, onSave);

  if (!isOpen) return null;

  return (
    <ModalPortal>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          fontFamily: '"Noto Sans", sans-serif'
        }}
      >
        <div
          style={{
            backgroundColor: colors.text.white,
            borderRadius: '0.75rem',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            width: '95%',
            maxWidth: '1200px',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <ModalHeader
            numeroFacture={numeroFacture}
            readOnly={readOnly}
            isManual={isManual}
            hasStoredDetails={hasStoredDetails}
            saving={saving}
            onClose={handleClose}
          />

          {/* Body */}
          <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
            {loading ? (
              <LoadingState />
            ) : isManual ? (
              <ManualEditContent
                intitule={intitule}
                setIntitule={setIntitule}
                dateEmission={dateEmissionManual}
                setDateEmission={setDateEmissionManual}
                montantHT={montantHTManual}
                setMontantHT={setMontantHTManual}
                tauxTVA={tauxTVAManual}
                setTauxTVA={setTauxTVAManual}
                readOnly={readOnly}
              />
            ) : shouldShowNoDetails ? (
              <NoDetailsState statut={statut} />
            ) : (
              <DetailsContent
                intitule={intitule}
                setIntitule={setIntitule}
                readOnly={readOnly}
                tempsDetails={tempsDetails}
                ajustementDetails={ajustementDetails}
                details={details}
                editingIndex={editingIndex}
                onEdit={handleEditLine}
                onSave={handleSaveLine}
                onDelete={handleDeleteLine}
                onFieldChange={handleFieldChange}
                onAddTemps={handleAddTempsLine}
                onAddAjustement={handleAddAjustementLine}
                tauxTVA={tauxTVA}
                setTauxTVA={setTauxTVA}
                totals={totals}
              />
            )}
          </div>

          {/* Footer */}
          <ModalFooter
            readOnly={readOnly}
            shouldShowNoDetails={shouldShowNoDetails && !isManual}
            saving={saving}
            loading={loading}
            effectiveDetails={isManual ? [{ montant: parseFloat(montantHTManual) || 0 }] : effectiveDetails}
            onClose={handleClose}
            onSave={isManual ? handleSaveManuel : handleSave}
          />
        </div>
      </div>
    </ModalPortal>
  );
}

// === SOUS-COMPOSANTS ===

function ManualEditContent({ intitule, setIntitule, dateEmission, setDateEmission, montantHT, setMontantHT, tauxTVA, setTauxTVA, readOnly }) {
  const ht  = parseFloat(montantHT)  || 0;
  const taux = parseFloat(tauxTVA)   || 0;
  const tva  = ht * (taux / 100);
  const ttc  = ht + tva;

  const fieldStyle = {
    width: '100%',
    padding: '0.625rem 0.75rem',
    border: `1px solid ${colors.border?.light || '#e2e8f0'}`,
    borderRadius: '6px',
    fontSize: '0.875rem',
    boxSizing: 'border-box',
    backgroundColor: readOnly ? '#f8fafc' : '#fff',
  };
  const labelStyle = {
    display: 'block',
    marginBottom: '0.375rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: colors.text?.dark || '#1a202c',
  };
  const rowStyle = { marginBottom: '1rem' };

  return (
    <div style={{ maxWidth: '520px' }}>
      {/* Intitulé */}
      <div style={rowStyle}>
        <label style={labelStyle}>Intitulé</label>
        <input
          type="text"
          value={intitule}
          onChange={e => setIntitule(e.target.value)}
          readOnly={readOnly}
          placeholder="Intitulé de la facture"
          style={fieldStyle}
        />
      </div>

      {/* Date d'émission */}
      <div style={rowStyle}>
        <label style={labelStyle}>Date d'émission</label>
        <input
          type="date"
          value={dateEmission || ''}
          onChange={e => setDateEmission(e.target.value)}
          readOnly={readOnly}
          style={fieldStyle}
        />
      </div>

      {/* Montant HT + TVA */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
        <div>
          <label style={labelStyle}>Montant HT (€)</label>
          <input
            type="number"
            value={montantHT}
            onChange={e => setMontantHT(e.target.value)}
            readOnly={readOnly}
            min="0"
            step="0.01"
            placeholder="0.00"
            style={fieldStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>TVA (%)</label>
          <select
            value={tauxTVA}
            onChange={e => setTauxTVA(e.target.value)}
            disabled={readOnly}
            style={fieldStyle}
          >
            <option value="0">0%</option>
            <option value="5.5">5.5%</option>
            <option value="10">10%</option>
            <option value="20">20%</option>
          </select>
        </div>
      </div>

      {/* Récapitulatif */}
      {ht > 0 && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#f8fafc',
          borderRadius: '6px',
          border: `1px solid ${colors.border?.light || '#e2e8f0'}`,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            <span style={{ color: colors.text?.secondary || '#64748b' }}>Montant HT :</span>
            <span>{ht.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
            <span style={{ color: colors.text?.secondary || '#64748b' }}>TVA ({taux}%) :</span>
            <span>{tva.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</span>
          </div>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            paddingTop: '0.5rem', borderTop: `1px solid ${colors.border?.light || '#e2e8f0'}`,
            fontWeight: '600', color: colors.primary?.teal || '#0d9488',
          }}>
            <span>Total TTC :</span>
            <span>{ttc.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function ModalHeader({ numeroFacture, readOnly, isManual, hasStoredDetails, saving, onClose }) {
  const subtitle = isManual
    ? 'Facture créée manuellement'
    : readOnly
      ? 'Mode consultation uniquement'
      : hasStoredDetails
        ? 'Détails enregistrés'
        : 'Détails calculés automatiquement (non enregistrés)';

  return (
    <div style={{
      position: 'sticky',
      top: 0,
      backgroundColor: colors.text.white,
      zIndex: 10,
      padding: '1.5rem',
      borderBottom: `1px solid ${colors.secondary.lightGray}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexShrink: 0
    }}>
      <div>
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600', color: colors.primary.gray }}>
          Détail de la facture {numeroFacture}
        </h2>
        <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: colors.secondary.mediumGray }}>
          {subtitle}
        </p>
      </div>
      <button
        onClick={onClose}
        disabled={saving}
        style={{
          background: 'none',
          border: 'none',
          cursor: saving ? 'not-allowed' : 'pointer',
          padding: '0.5rem',
          color: colors.secondary.mediumGray,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '0.375rem',
          transition: 'background-color 200ms'
        }}
        onMouseEnter={(e) => { if (!saving) e.currentTarget.style.backgroundColor = colors.secondary.lightGray; }}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
      >
        <X size={24} />
      </button>
    </div>
  );
}

function LoadingState() {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '2rem',
      color: colors.secondary.mediumGray
    }}>
      Chargement...
    </div>
  );
}

function NoDetailsState({ statut }) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '4rem 2rem',
      color: colors.secondary.mediumGray
    }}>
      <AlertTriangle size={64} style={{ marginBottom: '1.5rem', color: colors.primary.gold }} />
      <p style={{ fontSize: '1.25rem', fontWeight: '600', margin: '0 0 0.5rem 0', color: colors.primary.gray }}>
        Aucun détail renseigné
      </p>
      <p style={{ fontSize: '0.9375rem', margin: 0, textAlign: 'center', maxWidth: '500px' }}>
        Cette facture au statut "{statut}" n'a pas de détails enregistrés.
      </p>
    </div>
  );
}

function DetailsContent({
  intitule, setIntitule, readOnly,
  tempsDetails, ajustementDetails, details, editingIndex,
  onEdit, onSave, onDelete, onFieldChange,
  onAddTemps, onAddAjustement,
  tauxTVA, setTauxTVA, totals
}) {
  return (
    <>
      {/* Champ intitulé */}
      <div style={{ marginBottom: '2rem' }}>
        <label style={{
          display: 'block',
          marginBottom: '0.5rem',
          fontWeight: '600',
          color: colors.primary.gray,
          fontSize: '0.9375rem'
        }}>
          Intitulé de la facture
        </label>
        <input
          type="text"
          value={intitule}
          onChange={(e) => setIntitule(e.target.value)}
          disabled={readOnly}
          placeholder="Prestations du mois de ..."
          style={{
            width: '100%',
            padding: '0.75rem',
            border: `2px solid ${colors.secondary.lightTeal}`,
            borderRadius: '0.5rem',
            fontSize: '1rem',
            fontFamily: '"Noto Sans", sans-serif',
            outline: 'none',
            backgroundColor: readOnly ? colors.secondary.lightGray : colors.text.white,
            cursor: readOnly ? 'not-allowed' : 'text',
            opacity: readOnly ? 0.7 : 1,
            boxSizing: 'border-box'
          }}
          onFocus={(e) => { if (!readOnly) e.target.style.borderColor = colors.primary.teal; }}
          onBlur={(e) => e.target.style.borderColor = colors.secondary.lightTeal}
        />
      </div>

      {/* Tableau des lignes de temps */}
      <FactureDetailTable
        type="temps"
        items={tempsDetails}
        allDetails={details}
        editingIndex={editingIndex}
        readOnly={readOnly}
        onEdit={onEdit}
        onSave={onSave}
        onDelete={onDelete}
        onFieldChange={onFieldChange}
        onAdd={onAddTemps}
      />

      {/* Tableau des ajustements */}
      <FactureDetailTable
        type="ajustement"
        items={ajustementDetails}
        allDetails={details}
        editingIndex={editingIndex}
        readOnly={readOnly}
        onEdit={onEdit}
        onSave={onSave}
        onDelete={onDelete}
        onFieldChange={onFieldChange}
        onAdd={onAddAjustement}
      />

      {/* Totaux */}
      <FactureTotaux
        tauxTVA={tauxTVA}
        onTauxTVAChange={setTauxTVA}
        totals={totals}
        readOnly={readOnly}
      />
    </>
  );
}

function ModalFooter({ readOnly, shouldShowNoDetails, saving, loading, effectiveDetails, onClose, onSave }) {
  const isDisabled = saving || loading || effectiveDetails.length === 0;

  const buttonBase = {
    padding: '0.75rem 1.5rem',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 200ms',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  };

  return (
    <div style={{
      padding: '1.5rem',
      borderTop: `1px solid ${colors.secondary.lightGray}`,
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
      flexShrink: 0
    }}>
      <button
        onClick={onClose}
        disabled={saving}
        style={{
          ...buttonBase,
          backgroundColor: colors.text.white,
          color: colors.primary.gray,
          border: `2px solid ${colors.secondary.lightGray}`,
          opacity: saving ? 0.5 : 1,
          cursor: saving ? 'not-allowed' : 'pointer'
        }}
        onMouseEnter={(e) => { if (!saving) e.currentTarget.style.backgroundColor = colors.secondary.lightGray; }}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.text.white}
      >
        {readOnly ? 'Fermer' : 'Annuler'}
      </button>

      {!readOnly && !shouldShowNoDetails && (
        <>
          <button
            onClick={() => onSave(false)}
            disabled={isDisabled}
            style={{
              ...buttonBase,
              backgroundColor: colors.secondary.mediumGray,
              color: colors.text.white,
              border: 'none',
              opacity: isDisabled ? 0.5 : 1,
              cursor: isDisabled ? 'not-allowed' : 'pointer'
            }}
            onMouseEnter={(e) => { if (!isDisabled) e.currentTarget.style.opacity = '0.8'; }}
            onMouseLeave={(e) => { if (!isDisabled) e.currentTarget.style.opacity = '1'; }}
          >
            <Save size={18} />
            {saving ? 'Enregistrement...' : 'Enregistrer brouillon'}
          </button>

          <button
            onClick={() => onSave(true)}
            disabled={isDisabled}
            style={{
              ...buttonBase,
              backgroundColor: colors.primary.teal,
              color: colors.text.white,
              border: 'none',
              opacity: isDisabled ? 0.5 : 1,
              cursor: isDisabled ? 'not-allowed' : 'pointer'
            }}
            onMouseEnter={(e) => { if (!isDisabled) e.currentTarget.style.backgroundColor = '#005f6a'; }}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.primary.teal}
          >
            <Save size={18} />
            {saving ? 'Validation...' : 'Valider pour envoi'}
          </button>
        </>
      )}
    </div>
  );
}

export default FactureDetailModal;
